import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import SortIcon from './SortIcon'
import {
  compareNumber, compareText, nextSort, text, thClass,
  type SortDir,
} from './topTableSort'

/**
 * Top merker-grid shared by Klasse-detalj, Selger-detalj and Kjøper-detalj
 * (#481). The three pages render the same brand breakdown, so the column set
 * and the sorting live here once.
 *
 * The "Topp modell" column moved out in #586: collapsing a brand to its single
 * biggest model answered "what does Toyota mostly sell here?" but hid every
 * other model it moved. That question now has its own grid — see
 * TopModellerTable — and this one is purely the brand rollup.
 *
 * Every column sorts (AC2), cycling asc → desc → back to the server's order
 * (count desc), which is the same interaction KlasserTable uses.
 */

export interface TopMerkeRow {
  merke: string
  count: number
  share_pct: number
}

type SortCol = 'merke' | 'count' | 'share_pct'
type Sort = { col: SortCol; dir: SortDir } | null

const _nb = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 1 })

export function compare(a: TopMerkeRow, b: TopMerkeRow, col: SortCol, dir: SortDir): number {
  if (col === 'merke') return compareText(a.merke, b.merke, dir)
  return compareNumber(col === 'count' ? a.count : a.share_pct, col === 'count' ? b.count : b.share_pct, dir)
}

interface Props {
  rows: TopMerkeRow[]
}

export default function TopMerkerTable({ rows }: Props) {
  const { t } = useTranslation('bruktbil')
  const [sort, setSort] = useState<Sort>(null)

  const sorted = sort ? [...rows].sort((a, b) => compare(a, b, sort.col, sort.dir)) : rows

  const columns: { col: SortCol; label: string; right?: boolean }[] = [
    { col: 'merke', label: t('common.merke') },
    { col: 'count', label: t('common.antall'), right: true },
    { col: 'share_pct', label: t('common.andelPct'), right: true },
  ]

  if (rows.length === 0) {
    return <div className="card p-6 text-sm text-gray-400 dark:text-gray-500">{t('common.ingenData')}</div>
  }

  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
          <tr>
            {columns.map(c => (
              <th
                key={c.col}
                className={`${thClass}${c.right ? ' text-right' : ''}`}
                onClick={() => setSort(s => nextSort(s, c.col))}
              >
                {c.label} <SortIcon active={sort?.col === c.col} dir={sort?.dir ?? 'asc'} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => (
            <tr key={r.merke} className={i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}>
              <td className="px-3 py-1.5">{text(r.merke)}</td>
              <td className="px-3 py-1.5 tabular-nums text-right">{_nb.format(r.count)}</td>
              <td className="px-3 py-1.5 tabular-nums text-right">{_nb.format(r.share_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
